/**
 * Contexte du socle (après connexion) : utilisateur, préférences synchronisées, filiale courante, catalogue et droits,
 * client API. Les écrans métier y accèdent par les hooks exportés par web/src/socle/index.ts.
 */
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  HORS_MENU, TOUTES_FILIALES, filiale as trouverFiliale, normaliserPreferences, preferencesParDefaut,
  type CatalogueReponse, type Moi, type NoeudCatalogue, type Preferences,
} from '@agora/shared';
import type { ConfigApp } from './config';
import { useSession } from './auth/session';
import { creerClient, type ClientApi } from './api/client';
import { langueNavigateur, useT } from './i18n';
import { useZoom } from './zoom';

/* ---------------------------------------------------------------- configuration */
const CtxConfig = React.createContext<ConfigApp | null>(null);
export const ConfigProvider = CtxConfig.Provider;
export function useConfig(): ConfigApp {
  const c = React.useContext(CtxConfig);
  if (!c) throw new Error('useConfig hors de ConfigProvider');
  return c;
}

/* ---------------------------------------------------------------- client API (disponible dès la connexion) */
export function useClientApi(): ClientApi {
  const config = useConfig();
  const { jetonValide, fermer } = useSession();
  const { langue } = useT();
  const langueRef = React.useRef(langue);
  langueRef.current = langue;
  return React.useMemo(() => creerClient(
    config.apiUrl, jetonValide, () => void fermer({ distante: false }), () => langueRef.current,
    // Mode maquette chargé à la demande : la maquette (et zod, qu'elle utilise) n'alourdit pas le bundle principal.
    config.maquette ? (methode, url, corps) => import('./api/maquette').then((m) => m.transportMaquette(methode, url, corps)) : undefined,
  ), [config, jetonValide, fermer]);
}

/* ---------------------------------------------------------------- socle */
export interface ContexteSocle {
  config: ConfigApp;
  api: ClientApi;
  moi: Moi;
  preferences: Preferences;
  /** Met à jour les préférences (état local immédiat, enregistrement serveur différé de 0,8 s). */
  modifierPreferences: (maj: (p: Preferences) => Preferences) => void;
  /** Filiale courante (code ou ALL) : contexte des droits et des listes. */
  filiale: string;
  filialesAutorisees: string[];
  changerFiliale: (code: string) => void;
  /** Catalogue des menus de la filiale courante (sans les nœuds HORS_MENU, ouverts ailleurs). */
  catalogue?: CatalogueReponse;
  /** Index id → nœud du catalogue (droits inclus) pour la filiale courante, nœuds hors menu compris. */
  noeuds: Map<string, NoeudCatalogue>;
  deconnecter: () => void;
}

const Ctx = React.createContext<ContexteSocle | null>(null);

const CLE_FILIALE = 'agora.filiale';
const cleCachePrefs = (id: string) => `agora.preferences.${id}`;

/** Nœud sans ses descendants hors menu (HORS_MENU du catalogue) : barre latérale, accueil du métier, dock, recherche. */
function pourLesMenus(n: NoeudCatalogue): NoeudCatalogue {
  return { ...n, enfants: n.enfants.filter((e) => !HORS_MENU.has(e.id)).map(pourLesMenus) };
}

/** Catalogue des menus : celui de la filiale, sans les nœuds hors menu. */
function menus(c?: CatalogueReponse): CatalogueReponse | undefined {
  return c && { ...c, metiers: c.metiers.map(pourLesMenus) };
}

/** Index de TOUS les nœuds (hors menu compris : droits et écran restent), leurs enfants limités à ceux des menus. */
function indexer(c?: CatalogueReponse): Map<string, NoeudCatalogue> {
  const m = new Map<string, NoeudCatalogue>();
  const ajouter = (n: NoeudCatalogue) => { m.set(n.id, pourLesMenus(n)); n.enfants.forEach(ajouter); };
  if (c) [...c.metiers, ...c.dimensions, ...c.pilotage, ...c.outils].forEach(ajouter);
  return m;
}

function appliquerTheme(theme: Preferences['theme']) {
  const racine = document.documentElement;
  if (theme === 'clair') racine.setAttribute('data-theme', 'light');
  else if (theme === 'sombre') racine.setAttribute('data-theme', 'dark');
  else racine.removeAttribute('data-theme');
}

/** Charge Moi + préférences, puis fournit le socle ; `chargement` est affiché en attendant. */
export function SocleProvider({ chargement, erreur, children }: { chargement: React.ReactNode; erreur: (e: unknown, reessayer: () => void) => React.ReactNode; children: React.ReactNode }) {
  const config = useConfig();
  const api = useClientApi();
  const { fermer } = useSession();
  const { definirLangue } = useT();
  const qc = useQueryClient();

  const qMoi = useQuery({ queryKey: ['moi'], queryFn: () => api.get<Moi>('/v1/moi'), staleTime: 5 * 60_000 });
  const qPrefs = useQuery({
    queryKey: ['preferences'], queryFn: async () => normaliserPreferences(await api.get<Preferences>('/v1/preferences')), staleTime: Infinity,
  });

  const [prefs, setPrefs] = React.useState<Preferences | null>(null);
  const modifieLocalement = React.useRef(false);
  React.useEffect(() => {
    if (qPrefs.data && !modifieLocalement.current) setPrefs(qPrefs.data);
  }, [qPrefs.data]);
  // Affichage immédiat avec la dernière copie locale pendant le chargement serveur.
  React.useEffect(() => {
    if (prefs || !qMoi.data) return;
    try {
      const cache = localStorage.getItem(cleCachePrefs(qMoi.data.id));
      if (cache && !qPrefs.data) setPrefs(normaliserPreferences(JSON.parse(cache)));
    } catch { /* ignoré */ }
  }, [qMoi.data, prefs, qPrefs.data]);

  const minuteur = React.useRef<number | undefined>(undefined);
  const modifierPreferences = React.useCallback((maj: (p: Preferences) => Preferences) => {
    modifieLocalement.current = true;
    setPrefs((p) => {
      const n = normaliserPreferences(maj(p || preferencesParDefaut()));
      window.clearTimeout(minuteur.current);
      minuteur.current = window.setTimeout(() => {
        api.put<Preferences>('/v1/preferences', n)
          .then((enr) => qc.setQueryData(['preferences'], normaliserPreferences(enr)))
          .catch((e) => console.warn('[AGORA] préférences non enregistrées', e));
      }, 800);
      return n;
    });
  }, [api, qc]);

  const moi = qMoi.data;
  React.useEffect(() => {
    if (moi && prefs) try { localStorage.setItem(cleCachePrefs(moi.id), JSON.stringify(prefs)); } catch { /* ignoré */ }
  }, [moi, prefs]);

  // Filiale courante : choix de l'onglet, sinon filiale par défaut, sinon toutes (ou l'unique filiale autorisée).
  const autorisees = React.useMemo(() => moi?.filiales || [], [moi]);
  const [filialeChoisie, setFilialeChoisie] = React.useState<string | null>(() => {
    try { return sessionStorage.getItem(CLE_FILIALE); } catch { return null; }
  });
  const valide = (c?: string | null) => !!c && (c === TOUTES_FILIALES ? autorisees.length > 1 : autorisees.includes(c));
  const filiale = valide(filialeChoisie) ? filialeChoisie! : valide(prefs?.filiale) ? prefs!.filiale! : autorisees.length === 1 ? autorisees[0] : TOUTES_FILIALES;
  const changerFiliale = React.useCallback((code: string) => {
    setFilialeChoisie(code);
    try { sessionStorage.setItem(CLE_FILIALE, code); } catch { /* ignoré */ }
  }, []);

  // Langue effective, thème et zoom de l'affichage.
  React.useEffect(() => {
    if (!prefs) return;
    const l = prefs.langue || (prefs.filiale && prefs.filiale !== TOUTES_FILIALES ? trouverFiliale(prefs.filiale)?.langue : undefined) || langueNavigateur();
    if (l) definirLangue(l);
  }, [prefs, definirLangue]);
  React.useEffect(() => { if (prefs) appliquerTheme(prefs.theme); }, [prefs]);
  useZoom(prefs?.zoom);

  const qCatalogue = useQuery({
    queryKey: ['catalogue', filiale], enabled: !!moi,
    queryFn: () => api.get<CatalogueReponse>('/v1/catalogue', { filiale }), staleTime: 5 * 60_000, placeholderData: (precedent) => precedent,
  });
  const index = React.useMemo(() => indexer(qCatalogue.data), [qCatalogue.data]);
  const catalogue = React.useMemo(() => menus(qCatalogue.data), [qCatalogue.data]);

  const deconnecter = React.useCallback(() => {
    qc.clear();
    void fermer();
  }, [fermer, qc]);

  const valeur = React.useMemo<ContexteSocle | null>(() => (moi && prefs ? {
    config, api, moi, preferences: prefs, modifierPreferences, filiale, filialesAutorisees: autorisees, changerFiliale,
    catalogue, noeuds: index, deconnecter,
  } : null), [config, api, moi, prefs, modifierPreferences, filiale, autorisees, changerFiliale, catalogue, index, deconnecter]);

  const enErreur = qMoi.error || (qPrefs.error && !prefs);
  if (enErreur) return <>{erreur(enErreur, () => { void qMoi.refetch(); void qPrefs.refetch(); })}</>;
  if (!valeur) return <>{chargement}</>;
  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
}

export function useSocle(): ContexteSocle {
  const c = React.useContext(Ctx);
  if (!c) throw new Error('useSocle hors de SocleProvider (écran non protégé ?)');
  return c;
}
